import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PREVIEW_ENHANCED_RACE_RESULTS } from '@shared/graphql/queries';
import { MainLayout } from '../layouts';
import { Meeting } from '../types';
import { formatDateDisplay, formatDatePath } from '../utils';
import { ResultsTable } from '../components/results/ResultsTable';
import analyticsService from '../services/analytics';
import '../styles/ResultsPage.css';

const PreviewResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { dateString, trackName } = useParams<{ dateString?: string; trackName?: string }>();
  
  // Track if we've already logged a page view for this session
  const hasTrackedPageView = useRef(false);
  
  // State for selected race and view options
  const [selectedRace, setSelectedRace] = useState<number>(1);
  const [showAnalysis, setShowAnalysis] = useState<boolean>(true);
  const [showOnlyPicks, setShowOnlyPicks] = useState<boolean>(false);
  
  // Calculate the selected date or default to yesterday
  const getSelectedDate = (): Date => {
    if (dateString && /^\d{8}$/.test(dateString)) {
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6)) - 1;
      const day = parseInt(dateString.substring(6, 8));
      return new Date(year, month, day);
    }
    
    // Default to yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };
  
  const selectedDate = getSelectedDate();
  
  // Validate date is within allowed range (yesterday to 14 days ago)
  const validateDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const fourteenDaysAgo = new Date(today);
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    
    return date <= yesterday && date >= fourteenDaysAgo;
  };
  
  // If no date provided or invalid date, redirect to yesterday
  useEffect(() => {
    if (!dateString || !validateDate(selectedDate)) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      navigate(`/preview/${formatDatePath(yesterday)}`, { replace: true });
    }
  }, [dateString, selectedDate, navigate]);
  
  // Format date for API (timezone-safe)
  const formattedDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
  
  // Query enhanced race results for the selected date and track
  const { loading, error, data } = useQuery(GET_PREVIEW_ENHANCED_RACE_RESULTS, {
    variables: { 
      date: formattedDate,
      trackName: trackName || ''
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
    skip: !validateDate(selectedDate) || !trackName
  });
  
  // Refs for scrolling to races
  const raceRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  
  // Calculate total performance summary
  const calculateTotalPerformance = () => {
    if (!data?.previewEnhancedRaceResults || data.previewEnhancedRaceResults.length === 0) {
      return {
        total_runners: 0,
        total_analyzed: 0,
        top_pick_roi: 0,
        value_pick_roi: 0,
        first_pick_wins: 0,
        first_pick_places: 0,
        first_pick_total: 0,
        second_pick_wins: 0,
        second_pick_places: 0,
        second_pick_total: 0,
        third_pick_wins: 0,
        third_pick_places: 0,
        third_pick_total: 0,
        value_pick_wins: 0,
        value_pick_places: 0,
        value_pick_total: 0,
        overall_winners_found: 0,
        overall_races_with_selections: 0,
        overall_placed_selections: 0,
        overall_total_selections: 0
      };
    }
    
    // Initialize counters
    let totalRunners = 0;
    let totalAnalyzed = 0;
    let topPickWins = 0;
    let topPickStakes = 0;
    let valuePickWins = 0;
    let valuePickStakes = 0;
    
    // Initialize strike rate counters
    let firstPickWins = 0;
    let firstPickPlaces = 0;
    let firstPickTotal = 0;
    
    let secondPickWins = 0;
    let secondPickPlaces = 0;
    let secondPickTotal = 0;
    
    let thirdPickWins = 0;
    let thirdPickPlaces = 0;
    let thirdPickTotal = 0;
    
    let valuePickWinsCount = 0;
    let valuePickPlacesCount = 0;
    let valuePickTotal = 0;
    
    let overallWinnersFound = 0;
    let overallRacesWithSelections = 0;
    let overallPlacedSelections = 0;
    let overallTotalSelections = 0;
    
    // Calculate totals
    data.previewEnhancedRaceResults.forEach((race: any) => {
      if (!race.results || race.results.length === 0) return;
      
      totalRunners += race.total_runners || 0;
      totalAnalyzed += race.total_analyzed || 0;
      
      // Check if race has any selections
      const hasSelections = race.results.some((r: any) => r.pick_type);
      if (hasSelections) {
        overallRacesWithSelections++;
        
        // Check if we found the winner in our selections
        const winnerFound = race.results.some((r: any) => 
          r.pick_type && (r.position === 1 || r.position_finish === 1)
        );
        if (winnerFound) {
          overallWinnersFound++;
        }
      }
      
      // Process first picks (top pick order 1)
      const firstPick = race.results.find((r: any) => r.pick_type === 'top' && r.pick_order === 1);
      if (firstPick) {
        firstPickTotal++;
        topPickStakes++;
        
        // Check if first pick won
        if (firstPick.position === 1 || firstPick.position_finish === 1) {
          firstPickWins++;
          // a winner still waiting for its SP is pending: no stake yet, rather than a loss
          if (Number(firstPick.price_sp) > 0) topPickWins += Number(firstPick.price_sp);
          else topPickStakes--;
        }
        
        // Check if first pick placed (positions 1-3)
        if ((firstPick.position >= 1 && firstPick.position <= 3) || 
            (firstPick.position_finish >= 1 && firstPick.position_finish <= 3)) {
          firstPickPlaces++;
        }
        
        // Add to overall selections count
        overallTotalSelections++;
        if ((firstPick.position >= 1 && firstPick.position <= 3) || 
            (firstPick.position_finish >= 1 && firstPick.position_finish <= 3)) {
          overallPlacedSelections++;
        }
      }
      
      // Process second picks (top pick order 2)
      const secondPick = race.results.find((r: any) => r.pick_type === 'top' && r.pick_order === 2);
      if (secondPick) {
        secondPickTotal++;
        
        // Check if second pick won
        if (secondPick.position === 1 || secondPick.position_finish === 1) {
          secondPickWins++;
        }
        
        // Check if second pick placed (positions 1-3)
        if ((secondPick.position >= 1 && secondPick.position <= 3) || 
            (secondPick.position_finish >= 1 && secondPick.position_finish <= 3)) {
          secondPickPlaces++;
        }
        
        // Add to overall selections count
        overallTotalSelections++;
        if ((secondPick.position >= 1 && secondPick.position <= 3) || 
            (secondPick.position_finish >= 1 && secondPick.position_finish <= 3)) {
          overallPlacedSelections++;
        }
      }
      
      // Process third picks (top pick order 3)
      const thirdPick = race.results.find((r: any) => r.pick_type === 'top' && r.pick_order === 3);
      if (thirdPick) {
        thirdPickTotal++;
        
        // Check if third pick won
        if (thirdPick.position === 1 || thirdPick.position_finish === 1) {
          thirdPickWins++;
        }
        
        // Check if third pick placed (positions 1-3)
        if ((thirdPick.position >= 1 && thirdPick.position <= 3) || 
            (thirdPick.position_finish >= 1 && thirdPick.position_finish <= 3)) {
          thirdPickPlaces++;
        }
        
        // Add to overall selections count
        overallTotalSelections++;
        if ((thirdPick.position >= 1 && thirdPick.position <= 3) || 
            (thirdPick.position_finish >= 1 && thirdPick.position_finish <= 3)) {
          overallPlacedSelections++;
        }
      }
      
      // Process value picks
      const valuePicks = race.results.filter((r: any) => r.pick_type === 'value');
      if (valuePicks.length > 0) {
        valuePickTotal += valuePicks.length;
        
        // Check for value pick wins
        const valuePickWinner = valuePicks.find((r: any) => 
          r.position === 1 || r.position_finish === 1
        );
        
        if (valuePickWinner) {
          valuePickWinsCount++;
        }
        
        // Check for value pick places
        const valuePickPlacers = valuePicks.filter((r: any) => 
          (r.position >= 1 && r.position <= 3) || 
          (r.position_finish >= 1 && r.position_finish <= 3)
        );
        
        valuePickPlacesCount += valuePickPlacers.length;
        
        // Add to overall selections count
        overallTotalSelections += valuePicks.length;
        overallPlacedSelections += valuePickPlacers.length;
        
        // For ROI calculation (using all value picks for stakes, but only winners for returns)
        valuePickStakes += valuePicks.length;
        
        // Add returns for all winning value picks
        valuePicks.forEach((valuePick: any) => {
          if (valuePick.position === 1 || valuePick.position_finish === 1) {
            if (Number(valuePick.price_sp) > 0) valuePickWins += Number(valuePick.price_sp);
            else valuePickStakes--;
          }
        });
      }
    });
    
    // Calculate ROIs
    const topPickROI = topPickStakes > 0 ? ((topPickWins / topPickStakes) - 1) * 100 : 0;
    const valuePickROI = valuePickStakes > 0 ? ((valuePickWins / valuePickStakes) - 1) * 100 : 0;
    
    return {
      total_runners: totalRunners,
      total_analyzed: totalAnalyzed,
      top_pick_roi: topPickROI,
      value_pick_roi: valuePickROI,
      first_pick_wins: firstPickWins,
      first_pick_places: firstPickPlaces,
      first_pick_total: firstPickTotal,
      second_pick_wins: secondPickWins,
      second_pick_places: secondPickPlaces,
      second_pick_total: secondPickTotal,
      third_pick_wins: thirdPickWins,
      third_pick_places: thirdPickPlaces,
      third_pick_total: thirdPickTotal,
      value_pick_wins: valuePickWinsCount,
      value_pick_places: valuePickPlacesCount,
      value_pick_total: valuePickTotal,
      overall_winners_found: overallWinnersFound,
      overall_races_with_selections: overallRacesWithSelections,
      overall_placed_selections: overallPlacedSelections,
      overall_total_selections: overallTotalSelections
    };
  };
  
  // Get total performance summary
  const totalPerformance = calculateTotalPerformance();
  
  // Track page view
  useEffect(() => {
    if (!hasTrackedPageView.current && !loading && trackName && dateString) {
      try {
        analyticsService.trackPageView('preview-results', trackName, formattedDate, {
          isAuthenticated: false,
          dateString: dateString || 'yesterday',
          trackName: trackName,
          isPreview: true,
          raceCount: data?.previewEnhancedRaceResults?.length || 0,
          hasResults: data?.previewEnhancedRaceResults?.some((race: any) => race.results?.length > 0) || false,
          totalRunners: totalPerformance.total_runners,
          totalAnalyzed: totalPerformance.total_analyzed,
          overallWinnersFound: totalPerformance.overall_winners_found
        });
        hasTrackedPageView.current = true;
        console.log('📊 Analytics: PreviewResultsPage view tracked');
      } catch (error) {
        console.error('Analytics error:', error);
      }
    }
  }, [loading, formattedDate, dateString, trackName, data, totalPerformance]);
  
  // Reset tracking flag when the date changes
  useEffect(() => {
    hasTrackedPageView.current = false;
  }, [formattedDate]);
  
  // Effect to scroll to selected race when it changes
  useEffect(() => {
    if (selectedRace && raceRefs.current[selectedRace]) {
      raceRefs.current[selectedRace]?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [selectedRace]);
  
  // Analytics functions
  const handleShowAnalysisChange = (checked: boolean) => {
    setShowAnalysis(checked);
    
    // Track analytics
    try {
      if (trackName && dateString) {
        analyticsService.trackEvent('checkbox_toggled', {
          pageType: 'preview-results',
          trackName: trackName,
          selectedDate: formattedDate,
          metadata: {
            checkboxType: 'show_analysis',
            newValue: checked,
            previousValue: !checked
          }
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };
  
  const handleShowOnlyPicksChange = (checked: boolean) => {
    setShowOnlyPicks(checked);
    
    // Track analytics
    try {
      if (trackName && dateString) {
        analyticsService.trackEvent('checkbox_toggled', {
          pageType: 'preview-results',
          trackName: trackName,
          selectedDate: formattedDate,
          metadata: {
            checkboxType: 'show_only_picks',
            newValue: checked,
            previousValue: !checked
          }
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };
  
  const handleRaceNavigation = (raceNumber: number) => {
    setSelectedRace(raceNumber);
    
    // Track analytics
    try {
      if (trackName && dateString) {
        analyticsService.trackEvent('race_navigation', {
          pageType: 'preview-results',
          trackName: trackName,
          raceNumber,
          selectedDate: formattedDate,
          metadata: {
            previousRace: selectedRace,
            navigationMethod: 'button_click'
          }
        });
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };
  
  // Function to format ROI display
  const formatROI = (roi: number) => {
    const color = roi >= 0 ? 'text-racing-green' : 'text-racing-red';
    return <span className={color}>{roi >= 0 ? '+' : ''}{roi.toFixed(1)}%</span>;
  };
  
  if (loading) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-gray-500">Loading historical race results...</p>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  if (error) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-racing-red">Error loading race results: {error.message}</p>
            <button
              onClick={() => navigate(`/preview/${dateString}`)}
              className="mt-4 bg-deep-navy text-white px-4 py-2 rounded hover:bg-deep-navy-dark transition-colors"
            >
              Back to Preview
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  if (!data?.previewEnhancedRaceResults || data.previewEnhancedRaceResults.length === 0) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-gray-500">No race results found for this date and track.</p>
            <button
              onClick={() => navigate(`/preview/${dateString}`)}
              className="mt-4 bg-deep-navy text-white px-4 py-2 rounded hover:bg-deep-navy-dark transition-colors"
            >
              Back to Preview
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="results-page p-4">
        {/* Back to Preview Link */}
        <div className="mb-4">
          <button 
            onClick={() => navigate(`/preview/${dateString}`)}
            className="text-deep-navy hover:text-racing-green"
          >
            ← Back to Preview
          </button>
        </div>
        
        {/* Track Header */}
        <div className="bg-white rounded-lg shadow-card mb-6">
          <div className="p-4 border-b border-racing-gray-darker">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold">{trackName?.toUpperCase()} Historical Results</h1>
                <p className="text-gray-600">{formatDateDisplay(selectedDate)}</p>
              </div>
              <button
                onClick={() => navigate('/register')}
                className="bg-racing-green text-white px-6 py-2 rounded-md text-sm font-bold hover:bg-racing-green-dark transition-colors"
              >
                Get Today's Tips
              </button>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="p-4 bg-racing-green/10 border-t border-racing-green/20">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div className="mb-2 sm:mb-0">
                <p className="text-sm text-gray-700">
                  <strong>Viewing historical results.</strong> Sign up to access today's live tips and results.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Meeting Performance Summary */}
        <div className="bg-white rounded-lg shadow-card mb-6">
          <div className="bg-deep-navy text-white p-4 rounded-t-lg">
            <h2 className="text-lg font-bold">Meeting Performance Summary</h2>
          </div>
          
          <div className="p-6">
            {/* ROI Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm text-gray-600 mb-2">Top Pick ROI</h3>
                <div className={`text-2xl font-bold ${totalPerformance.top_pick_roi >= 0 ? 'text-racing-green' : 'text-racing-red'}`}>
                  {totalPerformance.top_pick_roi >= 0 ? '+' : ''}{totalPerformance.top_pick_roi.toFixed(1)}%
                </div>
              </div>
              <div>
                <h3 className="text-sm text-gray-600 mb-2">Value Pick ROI</h3>
                <div className={`text-2xl font-bold ${totalPerformance.value_pick_roi >= 0 ? 'text-racing-green' : 'text-racing-red'}`}>
                  {totalPerformance.value_pick_roi >= 0 ? '+' : ''}{totalPerformance.value_pick_roi.toFixed(1)}%
                </div>
              </div>
            </div>
            
            {/* Tips Performance Summary */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-deep-navy mb-4">Tips Performance Summary</h3>
              
              {/* Overall Performance */}
              <div className="mb-6">
                <h4 className="font-semibold text-deep-navy mb-3">Overall Performance</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-600">Total Winners:</div>
                    <div className="text-xl font-bold text-deep-navy">
                      {totalPerformance.overall_winners_found}/{totalPerformance.overall_races_with_selections}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Places:</div>
                    <div className="text-xl font-bold text-deep-navy">
                      {totalPerformance.overall_placed_selections}/{totalPerformance.overall_total_selections} ({totalPerformance.overall_total_selections > 0 ? ((totalPerformance.overall_placed_selections / totalPerformance.overall_total_selections) * 100).toFixed(1) : '0.0'}%)
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Strike Rates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <div className="font-semibold text-deep-navy mb-2">1st Pick Strike Rate</div>
                    <div className="text-lg font-bold text-deep-navy">
                      {totalPerformance.first_pick_wins}/{totalPerformance.first_pick_total} ({totalPerformance.first_pick_total > 0 ? ((totalPerformance.first_pick_wins / totalPerformance.first_pick_total) * 100).toFixed(2) : '0.00'}%)
                    </div>
                    <div className="text-sm text-gray-600">
                      Win: {totalPerformance.first_pick_wins}, Place: {totalPerformance.first_pick_places}
                    </div>
                  </div>
                  
                  <div>
                    <div className="font-semibold text-deep-navy mb-2">3rd Pick Strike Rate</div>
                    <div className="text-lg font-bold text-deep-navy">
                      {totalPerformance.third_pick_wins}/{totalPerformance.third_pick_total} ({totalPerformance.third_pick_total > 0 ? ((totalPerformance.third_pick_wins / totalPerformance.third_pick_total) * 100).toFixed(2) : '0.00'}%)
                    </div>
                    <div className="text-sm text-gray-600">
                      Win: {totalPerformance.third_pick_wins}, Place: {totalPerformance.third_pick_places}
                    </div>
                  </div>
                </div>
                
                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <div className="font-semibold text-deep-navy mb-2">2nd Pick Strike Rate</div>
                    <div className="text-lg font-bold text-deep-navy">
                      {totalPerformance.second_pick_wins}/{totalPerformance.second_pick_total} ({totalPerformance.second_pick_total > 0 ? ((totalPerformance.second_pick_wins / totalPerformance.second_pick_total) * 100).toFixed(2) : '0.00'}%)
                    </div>
                    <div className="text-sm text-gray-600">
                      Win: {totalPerformance.second_pick_wins}, Place: {totalPerformance.second_pick_places}
                    </div>
                  </div>
                  
                  <div>
                    <div className="font-semibold text-deep-navy mb-2">Value Pick Strike Rate</div>
                    <div className="text-lg font-bold text-deep-navy">
                      {totalPerformance.value_pick_wins}/{totalPerformance.value_pick_total} ({totalPerformance.value_pick_total > 0 ? ((totalPerformance.value_pick_wins / totalPerformance.value_pick_total) * 100).toFixed(2) : '0.00'}%)
                    </div>
                    <div className="text-sm text-gray-600">
                      Win: {totalPerformance.value_pick_wins}, Place: {totalPerformance.value_pick_places}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Exotic Bets Performance */}
              {data?.previewEnhancedRaceResults && data.previewEnhancedRaceResults.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div>
                        <div className="font-semibold text-deep-navy mb-2">Quinella Success Rate</div>
                        <div className="text-lg font-bold text-deep-navy">
                          {/* Calculate quinella success from race data */}
                          {(() => {
                            let quinellaSuccess = 0;
                            let totalRaces = data.previewEnhancedRaceResults.length;
                            
                            data.previewEnhancedRaceResults.forEach((race: any) => {
                              const topPicks = race.results?.filter((r: any) => r.pick_type === 'top' && r.pick_order <= 2) || [];
                              if (topPicks.length >= 2) {
                                const positions = topPicks.map((p: any) => p.position || p.position_finish).filter((pos: any) => pos && pos <= 2);
                                if (positions.length >= 2) quinellaSuccess++;
                              }
                            });
                            
                            return `${quinellaSuccess}/${totalRaces}`;
                          })()}
                        </div>
                      </div>
                      
                      <div>
                        <div className="font-semibold text-deep-navy mb-2">First Four Success Rate</div>
                        <div className="text-lg font-bold text-deep-navy">
                          {(() => {
                            let firstFourSuccess = 0;
                            let totalRaces = data.previewEnhancedRaceResults.length;
                            
                            data.previewEnhancedRaceResults.forEach((race: any) => {
                              const topPicks = race.results?.filter((r: any) => r.pick_type === 'top' && r.pick_order <= 4) || [];
                              if (topPicks.length >= 4) {
                                const positions = topPicks.map((p: any) => p.position || p.position_finish).filter((pos: any) => pos && pos <= 4);
                                if (positions.length >= 4) firstFourSuccess++;
                              }
                            });
                            
                            return `${firstFourSuccess}/${totalRaces} (${totalRaces > 0 ? ((firstFourSuccess / totalRaces) * 100).toFixed(1) : '0.0'}%)`;
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column */}
                    <div className="space-y-4">
                      <div>
                        <div className="font-semibold text-deep-navy mb-2">Box Trifecta Success Rate</div>
                        <div className="text-lg font-bold text-deep-navy">
                          {(() => {
                            let trifectaSuccess = 0;
                            let totalRaces = data.previewEnhancedRaceResults.length;
                            
                            data.previewEnhancedRaceResults.forEach((race: any) => {
                              const topPicks = race.results?.filter((r: any) => r.pick_type === 'top' && r.pick_order <= 3) || [];
                              if (topPicks.length >= 3) {
                                const positions = topPicks.map((p: any) => p.position || p.position_finish).filter((pos: any) => pos && pos <= 3);
                                if (positions.length >= 3) trifectaSuccess++;
                              }
                            });
                            
                            return `${trifectaSuccess}/${totalRaces}`;
                          })()}
                        </div>
                      </div>
                      
                      <div>
                        <div className="font-semibold text-deep-navy mb-2">Box First Four Success Rate</div>
                        <div className="text-lg font-bold text-deep-navy">
                          {(() => {
                            let boxFirstFourSuccess = 0;
                            let totalRaces = data.previewEnhancedRaceResults.length;
                            
                            data.previewEnhancedRaceResults.forEach((race: any) => {
                              const allPicks = race.results?.filter((r: any) => r.pick_type) || [];
                              if (allPicks.length >= 4) {
                                const positions = allPicks.map((p: any) => p.position || p.position_finish).filter((pos: any) => pos && pos <= 4);
                                if (positions.length >= 4) boxFirstFourSuccess++;
                              }
                            });
                            
                            return `${boxFirstFourSuccess}/${totalRaces} (${totalRaces > 0 ? ((boxFirstFourSuccess / totalRaces) * 100).toFixed(1) : '0.0'}%)`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* View Options */}
        <div className="bg-white rounded-lg shadow-card mb-6 p-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showAnalysis}
                onChange={(e) => handleShowAnalysisChange(e.target.checked)}
                className="form-checkbox text-deep-navy"
              />
              <span>Show Analysis</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showOnlyPicks}
                onChange={(e) => handleShowOnlyPicksChange(e.target.checked)}
                className="form-checkbox text-deep-navy"
              />
              <span>Show Only Picks</span>
            </label>
          </div>
        </div>
        
        {/* Race Navigation - Sticky */}
        {data?.previewEnhancedRaceResults && data.previewEnhancedRaceResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-card mb-6 p-4 sticky top-0 z-10">
            <h2 className="text-lg font-bold mb-3">Race Navigation</h2>
            <div className="flex flex-wrap gap-2">
              {data.previewEnhancedRaceResults.map((race: any) => (
                <button
                  key={race.race_number}
                  className={`px-3 py-1 rounded ${selectedRace === race.race_number ? 'bg-deep-navy text-white' : 'bg-racing-gray hover:bg-racing-gray-dark'}`}
                  onClick={() => handleRaceNavigation(race.race_number)}
                >
                  Race {race.race_number}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* All Races Results */}
        {data?.previewEnhancedRaceResults && data.previewEnhancedRaceResults.map((race: any) => (
          <div 
            key={race.race_id}
            ref={el => {
              if (el) raceRefs.current[race.race_number] = el;
            }}
            className={`bg-white rounded-lg shadow-card mb-6 ${selectedRace === race.race_number ? 'ring-2 ring-deep-navy' : ''}`}
            id={`race-${race.race_number}`}
          >
            <div className="bg-deep-navy text-white p-3 rounded-t-lg">
              <h2 className="text-lg font-heading font-bold">Race {race.race_number} - {race.race_name}</h2>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="bg-racing-green px-2 py-0.5 rounded text-sm">{race.distance}m</span>
                <span className="bg-racing-green px-2 py-0.5 rounded text-sm">{race.class}</span>
                <span className="bg-racing-green px-2 py-0.5 rounded text-sm">
                  {new Date(race.race_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              {/* Results Table */}
              {race.results && race.results.length > 0 ? (
                <div className="overflow-x-auto">
                  <ResultsTable 
                    results={race.results.filter((result: any) => !showOnlyPicks || result.pick_type)}
                    className="w-full"
                  />
                  
                  {/* Analysis Section (if enabled) */}
                  {showAnalysis && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full compact-analysis-table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Pos</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Horse</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Pick Type</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Assessed</th>
                            <th className="p-2 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {race.results
                            .filter((result: any) => !showOnlyPicks || result.pick_type)
                            .filter((result: any) => result.analysis_summary)
                            .map((result: any, index: number) => (
                              <tr 
                                key={`analysis-${result.runner_id}`} 
                                className={`${index % 2 === 0 ? 'bg-racing-gray' : ''}`}
                              >
                                <td className="p-2">
                                  {result.position !== undefined && result.position !== 0 ? (
                                    <div className={`${result.position === 1 ? 'bg-green-500' : result.position === 2 ? 'bg-blue-500' : result.position === 3 ? 'bg-blue-400' : 'bg-gray-400'} text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs`}>
                                      {result.position}
                                    </div>
                                  ) : !result.price_sp ? (
                                    <span className="text-xs">SCR</span>
                                  ) : (
                                    <span className="text-xs">DNF</span>
                                  )}
                                </td>
                                <td className="p-2 font-medium">{result.horse_name}</td>
                                <td className="p-2">
                                  {result.pick_type === 'top' && (
                                    <span className="bg-deep-navy text-white px-2 py-0.5 rounded mr-1">
                                      Top {result.pick_order}
                                    </span>
                                  )}
                                  {result.pick_type === 'value' && (
                                    <span className="bg-racing-green text-white px-2 py-0.5 rounded">
                                      Value
                                    </span>
                                  )}
                                </td>
                                <td className="p-2">${result.assessed_odds?.toFixed(2)}</td>
                                <td className="p-2">{result.confidence}%</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-4">
                  <p className="text-gray-500">No results available for this race.</p>
                </div>
              )}
              
              {/* Individual Race Performance Summary */}
              <div className="mt-6 pt-4 border-t border-racing-gray-darker">
                <h3 className="text-md font-bold mb-2">Race Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Runners</p>
                    <p className="font-bold">{race.total_runners}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Analyzed</p>
                    <p className="font-bold">{race.total_analyzed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Top Pick ROI</p>
                    <p className="font-bold">{formatROI(race.top_pick_roi)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Value Pick ROI</p>
                    <p className="font-bold">{formatROI(race.value_pick_roi)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </MainLayout>
  );
};

export default PreviewResultsPage;
